'use client';

import { type HTMLAttributes, type ReactNode, useId } from 'react';
import type { FocusScopeProps } from '@radix-ui/react-focus-scope';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { clsx } from 'clsx';
import { AnimatePresence } from 'motion/react';
import { useMergedDialogOpenProps } from '../dialog/utils';
import { TypographyProviderReset } from '../typography/context';
import dropdownStyles from '../dropdown/dropdown.module.scss';
import styles from './popover.module.scss';

export type PopoverAlign = 'start' | 'center' | 'end';
export type PopoverSide = 'top' | 'right' | 'bottom' | 'left';

interface PopoverProps extends HTMLAttributes<HTMLDivElement> {
  activator: ReactNode;
  activatorIsPrimitive?: boolean;
  align?: PopoverAlign;
  alignOffset?: number;
  children: ReactNode;
  matchActivatorWidth?: boolean;
  // modal, when set to true, interaction with outside elements will be disabled and only popover content will be visible to screen readers.
  modal?: boolean;
  onOpen?: (newOpen: boolean) => void;
  onOpenAutoFocus?: FocusScopeProps['onMountAutoFocus'];
  open?: boolean;
  side?: PopoverSide;
  sideOffset?: number;
}

export const PopoverActivator = PopoverPrimitive.Trigger;

const defaultHandleOnOpenAutoFocus: FocusScopeProps['onMountAutoFocus'] = function handlePopoverOpenFocusFirstTabbable(e) {
  if (e.target && 'focus' in e.target) {
    e.preventDefault();

    (e.target as HTMLElement).focus();
  }
};

export function Popover({
  activator,
  activatorIsPrimitive = false,
  align = 'start',
  alignOffset,
  children,
  className,
  matchActivatorWidth = true,
  modal,
  onOpen: providedOnOpenChange,
  onOpenAutoFocus = defaultHandleOnOpenAutoFocus,
  open: providedIsOpen,
  side = 'bottom',
  sideOffset = 8,
  ...rest
}: PopoverProps) {
  const id = useId();
  const [isOpen, setIsOpen] = useMergedDialogOpenProps({ isOpen: providedIsOpen, onOpenChange: providedOnOpenChange });

  return (
    <TypographyProviderReset>
      <PopoverPrimitive.Root modal={modal} onOpenChange={setIsOpen} open={isOpen}>
        {activatorIsPrimitive ? activator : <PopoverActivator asChild>{activator}</PopoverActivator>}

        <AnimatePresence>
          {isOpen ? (
            <PopoverPrimitive.Portal forceMount key={id}>
              <PopoverPrimitive.Content
                align={align}
                alignOffset={alignOffset}
                className={clsx(dropdownStyles.dropdown, styles.popover, matchActivatorWidth && styles.matchWidth, className)}
                onOpenAutoFocus={onOpenAutoFocus}
                side={side}
                sideOffset={sideOffset}
                {...rest}
              >
                {children}
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          ) : null}
        </AnimatePresence>
      </PopoverPrimitive.Root>
    </TypographyProviderReset>
  );
}
